import { AVATAR_EMOJI_CATEGORIES } from '@awwyeah/shared';

export interface AvatarPickerProps {
  value: string;
  onChange: (emoji: string) => void;
}

/**
 * Curated emoji picker (2026-08-01: expanded from 12 to ~100 pure-emoji options across six
 * categories, plus a free-text fallback — "more cool icons... that doesn't bloat the app").
 * Each category scrolls horizontally so the whole set fits without a giant vertical grid.
 */
export function AvatarPicker({ value, onChange }: AvatarPickerProps): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {AVATAR_EMOJI_CATEGORIES.map((category) => (
        <div key={category.id}>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-3">
            {category.label}
          </h4>
          <div
            className="flex gap-2 overflow-x-auto pb-1"
            role="radiogroup"
            aria-label={`Pick an avatar — ${category.label}`}
            data-testid={`avatar-category-${category.id}`}
          >
            {category.emojis.map((emoji) => {
              const selected = value === emoji;
              return (
                <button
                  key={emoji}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={emoji}
                  onClick={() => onChange(emoji)}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xl transition ease-app ${
                    selected ? 'bg-raised ring-2 ring-line-strong' : 'ring-1 ring-line'
                  }`}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <label className="block text-sm">
        <span className="mb-1 block text-fg-3">Or type your own</span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={8}
          placeholder="🎩"
          className="w-24 rounded border border-line bg-bg px-2 py-2 text-lg text-fg"
          aria-label="Custom avatar emoji"
        />
      </label>
    </div>
  );
}
